// frontend/src/pages/maintenance/MaintenanceSettings.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Switch,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  CircularProgress,
  Divider,
} from '@mui/joy';
import { FloppyDisk as Save, Bell, Envelope as Mail, Clock } from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationSettingsApi } from '../../lib/maintenanceApi';
import type { MaintenanceNotificationSettingsForm } from '../../types/maintenance';

const MaintenanceSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<MaintenanceNotificationSettingsForm>({
    notify_on_checkout: true,
    notify_on_report_generated: true,
    notify_on_repair_item_found: false,
    notify_contract_owner: true,
    notify_assigned_user: true,
    send_customer_report: true,
    send_customer_reminder: true,
    customer_reminder_days: 1,
  });

  // Fetch settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['maintenance-notification-settings'],
    queryFn: () => notificationSettingsApi.get(),
  });

  // Update form when settings load
  useEffect(() => {
    if (settings) {
      setFormData({
        notify_on_checkout: settings.notify_on_checkout,
        notify_on_report_generated: settings.notify_on_report_generated,
        notify_on_repair_item_found: settings.notify_on_repair_item_found,
        notify_contract_owner: settings.notify_contract_owner,
        notify_assigned_user: settings.notify_assigned_user,
        send_customer_report: settings.send_customer_report,
        send_customer_reminder: settings.send_customer_reminder,
        customer_reminder_days: settings.customer_reminder_days,
      });
    }
  }, [settings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => notificationSettingsApi.update(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-notification-settings'] });
      setHasChanges(false);
    },
  });

  const handleChange = (field: keyof MaintenanceNotificationSettingsForm, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography level="h2">Maintenance Settings</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Configure notifications and preferences
          </Typography>
        </Box>
        {hasChanges && (
          <Button
            startDecorator={<Save size={18} />}
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
          >
            Save Changes
          </Button>
        )}
      </Box>

      {/* Internal Notifications */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            <Bell size={18} style={{ marginRight: 8 }} />
            Internal Notifications
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 3 }}>
            Control when your team receives notifications
          </Typography>

          <Stack spacing={3}>
            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Notify on Checkout</FormLabel>
                <FormHelperText>
                  Send notification when a technician checks out from an appointment
                </FormHelperText>
              </Box>
              <Switch
                checked={formData.notify_on_checkout}
                onChange={(e) => handleChange('notify_on_checkout', e.target.checked)}
              />
            </FormControl>

            <Divider />

            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Notify on Report Generated</FormLabel>
                <FormHelperText>
                  Send notification when a maintenance report is generated
                </FormHelperText>
              </Box>
              <Switch
                checked={formData.notify_on_report_generated}
                onChange={(e) => handleChange('notify_on_report_generated', e.target.checked)}
              />
            </FormControl>

            <Divider />

            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Notify on High-Severity Repair Item</FormLabel>
                <FormHelperText>
                  Send immediate notification when a high-severity repair is found
                </FormHelperText>
              </Box>
              <Switch
                checked={formData.notify_on_repair_item_found}
                onChange={(e) => handleChange('notify_on_repair_item_found', e.target.checked)}
              />
            </FormControl>

            <Divider />

            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Notify Contract Owner</FormLabel>
                <FormHelperText>Include the user who created the contract</FormHelperText>
              </Box>
              <Switch
                checked={formData.notify_contract_owner}
                onChange={(e) => handleChange('notify_contract_owner', e.target.checked)}
              />
            </FormControl>

            <Divider />

            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Notify Assigned Technician</FormLabel>
                <FormHelperText>Include the technician assigned to the appointment</FormHelperText>
              </Box>
              <Switch
                checked={formData.notify_assigned_user}
                onChange={(e) => handleChange('notify_assigned_user', e.target.checked)}
              />
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Customer Notifications */}
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            <Mail size={18} style={{ marginRight: 8 }} />
            Customer Notifications
          </Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 3 }}>
            Control what customers receive
          </Typography>

          <Stack spacing={3}>
            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Send Maintenance Report</FormLabel>
                <FormHelperText>
                  Email the maintenance report PDF to the customer after each visit
                </FormHelperText>
              </Box>
              <Switch
                checked={formData.send_customer_report}
                onChange={(e) => handleChange('send_customer_report', e.target.checked)}
              />
            </FormControl>

            <Divider />

            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Send Appointment Reminders</FormLabel>
                <FormHelperText>
                  Email customers before their scheduled appointments
                </FormHelperText>
              </Box>
              <Switch
                checked={formData.send_customer_reminder}
                onChange={(e) => handleChange('send_customer_reminder', e.target.checked)}
              />
            </FormControl>

            {formData.send_customer_reminder && (
              <FormControl sx={{ maxWidth: 200 }}>
                <FormLabel>
                  <Clock size={14} style={{ marginRight: 4 }} />
                  Days Before Appointment
                </FormLabel>
                <Input
                  type="number"
                  value={formData.customer_reminder_days}
                  onChange={(e) =>
                    handleChange('customer_reminder_days', Number(e.target.value))
                  }
                  slotProps={{ input: { min: 1, max: 30 } }}
                />
              </FormControl>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MaintenanceSettings;
